import { useState, useEffect, useCallback, useRef, useImperativeHandle, useMemo } from 'preact/hooks'
import { forwardRef } from 'preact/compat'
import { Mail } from 'lucide-preact'
import { TextBox, Select } from '@/components'
import { BRAND_CLASSES } from '@/constants/theme'
import { useVariableCatalog } from '@/hooks/useVariableCatalog'
import { POSTAL_ADDRESS_VARIABLE, toUnlayerMergeTags } from './emailMergeTags'
import { UnlayerDesigner, type UnlayerDesignerHandle } from './UnlayerDesigner'
import { ResetCopyToTemplateButton } from './ResetCopyToTemplateButton'
import { SaveStatusIndicator, type SaveStatus } from '@/components/common/SaveStatusIndicator'
import { ToggleRow } from '@/components/common/ToggleRow'
import { assetApi, type EmailTemplateOptionDto, type WebAssetViewDto } from '@/api/asset'
import { useToast } from '@/hooks/useToast'
import { hasEmailBody } from '@/utils/unlayerDesign'
import { getDisplayErrorMessages } from '@/utils/errorHandling'

export interface EmailCopyEditorPanelHandle {
    /** Pull the current design + HTML out of Unlayer and persist whatever differs from
     *  the last save. Used before promote-to-version so the cloned copy's null DraftHtml
     *  gets compiled and stored before the server tries to snapshot it. Resolves false,
     *  having saved nothing, while the editor hasn't loaded or its design is empty: the
     *  version must not be saved then (ORB-960). */
    flushAndSave: () => Promise<boolean>
    /** Compile and return the current Unlayer HTML WITHOUT persisting — used to test-send
     *  the live, unsaved design as an HtmlOverride. Empty string when the Unlayer iframe
     *  hasn't mounted yet. */
    getHtml: () => Promise<string>
}

function serializeForCompare(value: unknown): string {
    if (value == null) return ''
    try { return JSON.stringify(value) } catch { return '' }
}

interface EmailCopyEditorPanelProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
    asset: WebAssetViewDto
    editable?: boolean
    /** Called after a successful auto-save so the parent can refetch and pick up the
     *  new ETag and the latest DraftUpdatedAt. */
    onSaved?: () => Promise<void> | void
    /** Whether the copy's editor has loaded its design; false while it is starting or not shown. Until it has,
     *  an export returns `{}`, so the parent holds Save as Version (ORB-960). */
    onEditorReadyChange?: (ready: boolean) => void
}

/**
 * Email Copy editor panel — Subject input, Template selector, and the shared Unlayer
 * designer seeded from the copy's own DraftDesign. The copy owns its full Unlayer
 * design (cloned from the bound template when the binding was first set on the server
 * side), so the user can freely change alignment, add images, split paragraphs, etc.,
 * without restructuring the template.
 *
 * Autosave fires on subject blur, template change, and (debounced) on every Unlayer
 * design update — persisting DraftDesign + DraftHtml together.
 */
export const EmailCopyEditorPanel = forwardRef<EmailCopyEditorPanelHandle, EmailCopyEditorPanelProps>(
function EmailCopyEditorPanel({
    clientCompanyId,
    workspaceId,
    campaignId,
    webId,
    asset,
    editable = true,
    onSaved,
    onEditorReadyChange,
}, ref) {
    const [subject, setSubject] = useState(asset.Subject ?? '')
    const [subjectB, setSubjectB] = useState(asset.SubjectVariants?.[0]?.Subject ?? '')
    const [abEnabled, setAbEnabled] = useState((asset.SubjectVariants?.length ?? 0) > 0)
    const [templateAssetId, setTemplateAssetId] = useState<string | null>(asset.EmailTemplateAssetId ?? null)
    const [templates, setTemplates] = useState<EmailTemplateOptionDto[]>([])
    const [templatesLoading, setTemplatesLoading] = useState(false)
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
    const [saveError, setSaveError] = useState<string | null>(null)
    const { showError } = useToast()
    // A copy whose stored design is `{}` would open on a blank canvas, and saving that would replace its email
    // (ORB-960). It gets a notice pointing at Reset to template instead of the editor.
    const designIsEmpty = asset.DraftDesign != null && !hasEmailBody(asset.DraftDesign)
    const designerShown = !!templateAssetId && asset.DraftDesign != null && !designIsEmpty
    // The designer reports its own readiness while mounted; once it is gone there is no editor to be ready.
    useEffect(() => {
        if (!designerShown) onEditorReadyChange?.(false)
    }, [designerShown, onEditorReadyChange])

    // ORB-466: merge tags come from the web's variable catalog. Copies own their rendered
    // content directly, so unlike templates they expose no slot markers — only variables that
    // resolve in an email body for this web.
    const { data: variableCatalog } = useVariableCatalog(workspaceId, webId)
    const copyMergeTags = useMemo(
        () => toUnlayerMergeTags(variableCatalog?.Groups ?? [], 'EmailBody', [POSTAL_ADDRESS_VARIABLE]),
        [variableCatalog],
    )

    // Refs capture the latest in-flight values so the save closure reads them at fire
    // time rather than at construction time.
    const subjectRef = useRef(subject)
    const subjectBRef = useRef(subjectB)
    const templateRef = useRef(templateAssetId)
    subjectRef.current = subject
    subjectBRef.current = subjectB
    templateRef.current = templateAssetId

    // Pending refs hold the values awaiting persistence; the lastPersisted refs let us
    // tell our own save's refetch from a server-pushed change so we don't remount the
    // Unlayer canvas on every save round-trip.
    const pendingDesignRef = useRef<unknown>(null)
    const pendingHtmlRef = useRef<string | null>(null)
    const lastPersistedDesignRef = useRef<unknown>(asset.DraftDesign ?? null)
    const lastPersistedHtmlRef = useRef<string | null>(asset.DraftHtml ?? null)
    const designSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const designerRef = useRef<UnlayerDesignerHandle>(null)

    // Tracks which save path most recently failed so handleRetry can re-run the right one.
    // Pending refs alone aren't reliable: after a successful design save they stay non-null
    // (they're also used for the still-matches check), which would make a Retry after a
    // subject/template failure wrongly route to the no-op design save.
    const lastFailedPathRef = useRef<'design' | 'subjectTemplate' | null>(null)
    // One subject/template save at a time, so responses can't settle out of order (ORB-960). A change made while
    // one is in flight is sent once it settles.
    const subjectTemplateInFlightRef = useRef(false)
    const subjectTemplateQueuedRef = useRef(false)

    // Tracks what we last persisted across all fields so the external-change effect can
    // tell our own save's refetch (no-op) from a server-pushed change (e.g., Webster
    // rebuild, or a re-clone after a template re-bind) that should remount the editor.
    const lastPersistedRef = useRef({
        subject: asset.Subject ?? '',
        subjectB: asset.SubjectVariants?.[0]?.Subject ?? '',
        template: asset.EmailTemplateAssetId ?? null,
    })

    const [designerKey, setDesignerKey] = useState(0)
    useEffect(() => {
        const incoming = {
            subject: asset.Subject ?? '',
            subjectB: asset.SubjectVariants?.[0]?.Subject ?? '',
            template: asset.EmailTemplateAssetId ?? null,
        }
        const persisted = lastPersistedRef.current
        const subjectChanged = incoming.subject !== persisted.subject
        const subjectBChanged = incoming.subjectB !== persisted.subjectB
        const templateChanged = incoming.template !== persisted.template
        // Content equality, not referential. After our own save lands and the parent
        // refetches, asset.DraftDesign is a brand-new object parsed from the response
        // with identical content — referential `!==` would falsely report it as an
        // external change and remount the canvas. JSON-stringify is cheap and reliable
        // because the design is JSON-serialisable by definition (it's Unlayer's wire format).
        const designChanged = serializeForCompare(asset.DraftDesign ?? null) !== serializeForCompare(lastPersistedDesignRef.current)
        const htmlChanged = (asset.DraftHtml ?? null) !== lastPersistedHtmlRef.current

        if (!subjectChanged && !subjectBChanged && !templateChanged && !designChanged && !htmlChanged) return

        if (subjectChanged) setSubject(incoming.subject)
        if (subjectBChanged) {
            setSubjectB(incoming.subjectB)
            // Reflect the persisted challenger both ways: turn the A/B toggle on when one arrives, and off
            // when it's cleared server-side (e.g. reset-to-template) so the UI never shows the toggle on
            // with no second subject behind it. Local pre-save toggling is untouched — this only fires when
            // the persisted value actually changed.
            setAbEnabled(incoming.subjectB.length > 0)
        }
        if (templateChanged) setTemplateAssetId(incoming.template)
        if (designChanged || htmlChanged) {
            // Remount UnlayerDesigner so it picks up the new initialDesign — Unlayer's
            // iframe doesn't expose a way to swap designs without remount.
            lastPersistedDesignRef.current = asset.DraftDesign ?? null
            lastPersistedHtmlRef.current = asset.DraftHtml ?? null
            pendingDesignRef.current = null
            pendingHtmlRef.current = null
            if (designSaveTimerRef.current) clearTimeout(designSaveTimerRef.current)
            setDesignerKey(k => k + 1)
        }
        lastPersistedRef.current = incoming
    }, [asset.Subject, asset.SubjectVariants, asset.EmailTemplateAssetId, asset.DraftDesign, asset.DraftHtml])

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setTemplatesLoading(true)
            const response = await assetApi.getEmailTemplatesByWeb(clientCompanyId, workspaceId, campaignId, webId)
            if (cancelled) return
            if (response.data) setTemplates(response.data)
            setTemplatesLoading(false)
        }
        void load()
        return () => { cancelled = true }
    }, [clientCompanyId, workspaceId, campaignId, webId])

    const saveSubjectTemplateOnce = useCallback(async () => {
        setSaveStatus('saving')
        setSaveError(null)
        const snapshot = {
            subject: subjectRef.current,
            subjectB: subjectBRef.current,
            template: templateRef.current,
        }
        const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, asset.Id, {
            Id: asset.Id,
            CampaignId: asset.CampaignId,
            Subject: snapshot.subject,
            // A challenger persists only when the second subject has content; the server assigns its key.
            SubjectVariants: snapshot.subjectB.trim() ? [{ Subject: snapshot.subjectB }] : [],
            EmailTemplateAssetId: snapshot.template ?? '',
        })
        if (response.data) {
            lastPersistedRef.current = snapshot
            lastFailedPathRef.current = null
            const stillMatches =
                subjectRef.current === snapshot.subject &&
                subjectBRef.current === snapshot.subjectB &&
                templateRef.current === snapshot.template
            setSaveStatus(stillMatches ? 'saved' : 'dirty')
            await onSaved?.()
        } else {
            lastFailedPathRef.current = 'subjectTemplate'
            setSaveStatus('error')
            setSaveError(getDisplayErrorMessages(response.error, 'Save failed.').join(' '))
            // A refused binding (422, e.g. a template with an empty design) left nothing saved, so the
            // selector goes back to the template the copy is still bound to rather than showing the refused one —
            // unless the user has picked again while this save was in flight, since that newer choice is what's pending.
            if (response.status === 422 &&
                snapshot.template !== lastPersistedRef.current.template &&
                templateRef.current === snapshot.template) {
                templateRef.current = lastPersistedRef.current.template
                setTemplateAssetId(lastPersistedRef.current.template)
            }
        }
    }, [clientCompanyId, workspaceId, campaignId, asset.Id, asset.CampaignId, onSaved])

    const runSubjectTemplateSave = useCallback(async () => {
        if (subjectTemplateInFlightRef.current) {
            subjectTemplateQueuedRef.current = true
            return
        }
        subjectTemplateInFlightRef.current = true
        try {
            do {
                subjectTemplateQueuedRef.current = false
                await saveSubjectTemplateOnce()
            } while (subjectTemplateQueuedRef.current)
        } finally {
            subjectTemplateInFlightRef.current = false
        }
    }, [saveSubjectTemplateOnce])

    /** Persists the pending design and HTML. Resolves false when the update failed; nothing to save counts as saved. */
    const runDesignSave = useCallback(async (): Promise<boolean> => {
        const designSnapshot = pendingDesignRef.current
        const htmlSnapshot = pendingHtmlRef.current
        const designChanged = designSnapshot != null && designSnapshot !== lastPersistedDesignRef.current
        const htmlChanged = htmlSnapshot != null && htmlSnapshot !== lastPersistedHtmlRef.current
        if (!designChanged && !htmlChanged) return true

        setSaveStatus('saving')
        setSaveError(null)
        const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, asset.Id, {
            Id: asset.Id,
            CampaignId: asset.CampaignId,
            ...(designChanged && designSnapshot != null ? { DraftDesign: designSnapshot } : {}),
            ...(htmlChanged && htmlSnapshot != null ? { DraftHtml: htmlSnapshot } : {}),
        })
        if (response.data) {
            if (designSnapshot != null) lastPersistedDesignRef.current = designSnapshot
            if (htmlSnapshot != null) lastPersistedHtmlRef.current = htmlSnapshot
            lastFailedPathRef.current = null
            const stillMatches =
                pendingDesignRef.current === designSnapshot &&
                pendingHtmlRef.current === htmlSnapshot
            setSaveStatus(stillMatches ? 'saved' : 'dirty')
            await onSaved?.()
            return true
        }
        lastFailedPathRef.current = 'design'
        setSaveStatus('error')
        setSaveError(getDisplayErrorMessages(response.error, 'Save failed.').join(' '))
        return false
    }, [clientCompanyId, workspaceId, campaignId, asset.Id, asset.CampaignId, onSaved])

    const handleDesignUpdate = useCallback((design: unknown, html: string) => {
        pendingDesignRef.current = design
        pendingHtmlRef.current = html
        const designChanged = design !== lastPersistedDesignRef.current
        const htmlChanged = html !== lastPersistedHtmlRef.current
        if (!designChanged && !htmlChanged) return
        setSaveStatus('dirty')

        if (designSaveTimerRef.current) clearTimeout(designSaveTimerRef.current)
        designSaveTimerRef.current = setTimeout(() => { void runDesignSave() }, 800)
    }, [runDesignSave])

    useEffect(() => () => {
        if (designSaveTimerRef.current) clearTimeout(designSaveTimerRef.current)
    }, [])

    // Imperative handle for promote-to-version. Pulls the current design + HTML straight
    // from Unlayer (so a freshly cloned copy whose DraftHtml is null still gets compiled),
    // populates the pending refs, cancels any in-flight debounce, and runs the save. The
    // server-side promote that fires after this resolves now finds a populated DraftHtml
    // instead of failing the "Body Required" validation.
    useImperativeHandle(ref, () => ({
        flushAndSave: async () => {
            // No editor means no design to save yet (still being prepared, or empty), so there is nothing to promote.
            if (!designerRef.current) {
                showError('Not saved yet', "The email editor isn't open yet. Wait for the email to appear, then save.")
                return false
            }
            // An editor that hasn't loaded exports an empty design; saving it is what wiped a template (ORB-960).
            if (!designerRef.current.isReady()) {
                showError('Not saved yet', 'The email editor is still loading. Wait for the email to appear, then save.')
                return false
            }
            const design = await designerRef.current.getDesign()
            if (!hasEmailBody(design)) {
                showError('Not saved', "The editor returned an empty design, so nothing was saved. Reload the page and try again.")
                return false
            }
            const html = await designerRef.current.getHtml()
            pendingDesignRef.current = design
            pendingHtmlRef.current = html
            if (designSaveTimerRef.current) {
                clearTimeout(designSaveTimerRef.current)
                designSaveTimerRef.current = null
            }
            // A failed update leaves the last saved draft in place, and promoting would snapshot that instead.
            return runDesignSave()
        },
        // Live HTML for a test-send override — compiles the current design straight out of
        // Unlayer without touching the pending/persist refs, so it never triggers a save.
        getHtml: async () => (await designerRef.current?.getHtml()) ?? '',
    }), [runDesignSave, showError])

    const handleSubjectBlur = useCallback(() => {
        if (subjectRef.current !== lastPersistedRef.current.subject
            || subjectBRef.current !== lastPersistedRef.current.subjectB) {
            void runSubjectTemplateSave()
        }
    }, [runSubjectTemplateSave])

    const handleToggleAb = useCallback((next: boolean) => {
        setAbEnabled(next)
        // Turning it off drops the challenger on the next save; clear the field so the save sends [].
        if (!next && subjectBRef.current) {
            setSubjectB('')
            subjectBRef.current = ''
            setSaveStatus('dirty')
            void runSubjectTemplateSave()
        }
    }, [runSubjectTemplateSave])

    const handleTemplateChange = useCallback((e: Event) => {
        const value = (e.target as HTMLSelectElement).value || null
        setTemplateAssetId(value)
        templateRef.current = value
        if (value !== lastPersistedRef.current.template) {
            void runSubjectTemplateSave()
        }
    }, [runSubjectTemplateSave])

    const handleRetry = useCallback(() => {
        // Route to whichever save path most recently failed. Falls back to design save
        // if nothing's recorded (e.g., manual retry after a transient hiccup with no
        // prior error tracked) since that's the more frequent path.
        if (lastFailedPathRef.current === 'subjectTemplate') {
            void runSubjectTemplateSave()
        } else {
            void runDesignSave()
        }
    }, [runDesignSave, runSubjectTemplateSave])

    const templateName = templates.find(t => t.Id === templateAssetId)?.DisplayName

    return (
        <div className="flex flex-col h-full bg-white dark:bg-slate-900">
            {/* Form fields header */}
            <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 dark:border-slate-700 space-y-3 bg-slate-50/50 dark:bg-slate-800/30">
                <TextBox
                    name="subject"
                    label={abEnabled ? 'Subject A' : 'Subject'}
                    value={subject}
                    placeholder="Email subject line"
                    disabled={!editable}
                    onChange={(next) => { setSubject(next); setSaveStatus('dirty') }}
                    onBlur={handleSubjectBlur}
                />

                <ToggleRow
                    label="Add a second subject line"
                    checked={abEnabled}
                    onChange={handleToggleAb}
                    disabled={!editable}
                    size="sm"
                />

                {abEnabled && (
                    <TextBox
                        name="subjectB"
                        label="Subject B"
                        value={subjectB}
                        placeholder="Alternate subject line"
                        disabled={!editable}
                        onChange={(next) => { setSubjectB(next); setSaveStatus('dirty') }}
                        onBlur={handleSubjectBlur}
                    />
                )}

                {templates.length === 0 && !templatesLoading ? (
                    <div>
                        <label className={`block text-sm font-medium mb-1 ${BRAND_CLASSES.INPUT.LABEL}`}>Template</label>
                        <div className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300">
                            <Mail className="w-3.5 h-3.5" />
                            <span>No templates yet — create one in the Email Template editor for this campaign.</span>
                        </div>
                    </div>
                ) : (
                    <Select
                        name="template"
                        label={templatesLoading ? 'Template (loading…)' : 'Template'}
                        placeholder="— Select a template —"
                        value={templateAssetId ?? ''}
                        disabled={!editable || templatesLoading}
                        options={templates.map(t => ({ value: t.Id, label: t.DisplayName }))}
                        onChange={handleTemplateChange}
                    />
                )}
            </div>

            {/* Unlayer designer — fills remaining vertical space. */}
            <div className="flex-1 min-h-0">
                {templateAssetId && designIsEmpty ? (
                    <div role="alert" className="h-full flex items-center justify-center p-6">
                        <div className="max-w-sm text-center text-sm text-slate-500 dark:text-slate-400 space-y-2">
                            <p className="font-medium text-slate-700 dark:text-slate-200">This email's design is empty</p>
                            <p>Use <strong>Reset to template</strong> below to rebuild it from the bound template.</p>
                        </div>
                    </div>
                ) : designerShown ? (
                    <UnlayerDesigner
                        key={designerKey}
                        ref={designerRef}
                        initialDesign={asset.DraftDesign}
                        editable={editable}
                        onUpdate={handleDesignUpdate}
                        mergeTags={copyMergeTags}
                        className="h-full"
                        diagnosticsId={asset.Id}
                        onReadyChange={onEditorReadyChange}
                    />
                ) : (
                    <div className="h-full flex items-center justify-center p-6">
                        <div className="max-w-sm text-center text-sm text-slate-500 dark:text-slate-400 space-y-2">
                            <p className="font-medium text-slate-700 dark:text-slate-200">
                                {!templateAssetId ? 'Pick a template to start' : 'Loading editor…'}
                            </p>
                            <p>
                                {!templateAssetId
                                    ? 'Your email\'s design will be created from the template you select above.'
                                    : 'Your design is being prepared from the bound template.'}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer: template binding + reset action + save status. */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-800/30">
                <div className={`text-[11px] truncate ${BRAND_CLASSES.INPUT.HELPER}`}>
                    {templateName ? `Bound to "${templateName}"` : 'No template bound yet'}
                </div>
                <div className="flex items-center gap-2">
                    {templateAssetId && (
                        <ResetCopyToTemplateButton
                            clientCompanyId={clientCompanyId}
                            workspaceId={workspaceId}
                            campaignId={campaignId}
                            assetId={asset.Id}
                            disabled={!editable}
                            onReset={onSaved}
                        />
                    )}
                    <SaveStatusIndicator
                        status={saveStatus}
                        error={saveError}
                        onRetry={handleRetry}
                    />
                </div>
            </div>
        </div>
    )
})
